import { Box, IconButton, Tooltip } from '@mui/material';
import React from 'react';

import { CollapseIcon, ExpandIcon } from './navigation/SidebarIcons';

import { useDashboardLayout } from '@/context/DashboardLayoutProvider';

/* eslint-disable max-len */
export const HauserWordmark = ({ height = 28, color = "#fff" }: { height?: number; color?: string }) => (
  <svg
    height={height}
    viewBox="0 0 287.31 55.61"
    style={{ width: 'auto' }}
    xmlns="http://www.w3.org/2000/svg">
    <path
      fill={color}
      d="M19.7,55.45v-24.17h-9.85v24.17H0V.16h9.85v22.04h9.85V.16h9.85v55.29h-9.85Z"
    />
    <path
      fill={color}
      d="M124.14,55.61h-12.93c-4.97,0-8.28-3.4-8.28-8.37V.16h9.85v45.89c0,.16.08.32.32.32h9.22c.16,0,.32-.16.32-.32V.16h9.85v47.08c0,4.98-3.39,8.37-8.35,8.37Z"
    />
    <path
      fill={color}
      d="M173.8,38.23l-15.92-15.8c-2.29-2.21-3.47-4.9-3.47-8.29v-5.85c0-4.9,3.31-8.29,8.28-8.29h12.93c4.97,0,8.35,3.4,8.35,8.29v8.61h-9.85v-7.35c0-.24-.16-.32-.32-.32h-9.22c-.24,0-.32.08-.32.32v5.45c0,.32.08.47.32.71l15.92,15.8c2.21,2.21,3.47,4.9,3.47,8.29v7.42c0,4.98-3.39,8.37-8.35,8.37h-12.93c-4.97,0-8.28-3.4-8.28-8.37v-8.61h9.85v7.43c0,.16.08.32.32.32h9.22c.16,0,.32-.16.32-.32v-7.11c0-.32-.16-.47-.32-.71Z"
    />
    <path
      fill={color}
      d="M207.85,55.45V.16h25.61v9.08h-15.76v13.11h11.82v8.69h-11.82v15.32h15.76v9.08h-25.61Z"
    />
    <path
      fill={color}
      d="M276.98,55.45l-5.6-21.56h-4.57v21.56h-9.85V.16h21.2c4.96,0,8.35,3.4,8.35,8.29v17.14c0,4.03-2.21,7.03-5.83,7.98l6.62,21.88h-10.32ZM266.81,24.8h9.54c.16,0,.32-.08.32-.32v-14.93c0-.24-.16-.32-.32-.32h-9.54v15.56Z"
    />
    <path
      fill={color}
      d="M72.46,0h-12.42l-12.34,55.61h37.09L72.46,0ZM60.49,45.53l5.47-27.42.25-1.99.25,1.99,5.53,27.42h-11.5Z"
    />
  </svg>
);

const CircleLogo = () => (
  <svg height="32" viewBox="0 0 1015.61 1015.61" width="32">
    <circle cx="507.81" cy="507.81" r="507.81" fill="#c0ff02" />
    <path
      fill="#231f20"
      d="M569.85,230.61h-124.08l-123.26,554.4h370.61l-123.26-554.4ZM450.24,684.56l54.68-273.39,2.47-19.8,2.47,19.8,55.28,273.39h-114.91Z"
    />
  </svg>
);
/* eslint-enable max-len */

const sidebarBg = {
  backgroundColor: 'var(--tx-glass-panel)',
  backgroundImage: 'var(--tx-surface-light)',
  boxShadow: 'var(--tx-shadow)',
  borderRadius: '0 25px 25px 0',
  overflow: 'hidden',
};

interface SidebarShellProps {
  topContent?: React.ReactNode;
  children: React.ReactNode;
  footer: React.ReactNode;
}

export const SidebarShell = ({ topContent, children, footer }: SidebarShellProps) => {
  const { isLeftSidebarCollapsed, toggleLeftSidebar } = useDashboardLayout();

  if (isLeftSidebarCollapsed) {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          boxSizing: 'border-box',
          ...sidebarBg,
        }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            pt: '16px',
            pb: '26px',
            gap: '8px',
          }}>
          <CircleLogo />
          <Tooltip title="Expand sidebar" placement="right">
            <IconButton
              onClick={toggleLeftSidebar}
              size="small"
              sx={{
                p: '5px',
                color: 'var(--tx-muted)',
                '&:hover': {
                  backgroundColor: 'var(--tx-fill)',
                  color: 'var(--tx-muted)',
                },
              }}>
              <ExpandIcon color="currentColor" />
            </IconButton>
          </Tooltip>
        </Box>
        {topContent}
        <Box
          sx={{ flex: 1, overflow: 'auto', width: '100%', scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
          {children}
        </Box>
        {footer}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        boxSizing: 'border-box',
        px: '20px',
        ...sidebarBg,
      }}>
      <Box sx={{ pt: '22px', pb: '22px', display: 'flex', justifyContent: 'center' }}>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
          <HauserWordmark height={20} color="var(--tx-muted)" />
          <Tooltip title="Collapse sidebar">
            <IconButton
              size="small"
              onClick={toggleLeftSidebar}
              sx={{ color: 'var(--tx-faint)', p: 0.5, borderRadius: '9px', '&:hover': { color: 'var(--tx-text)', backgroundColor: 'var(--tx-fill)' } }}>
              <CollapseIcon color="currentColor" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      {topContent}
      <Box sx={{ flex: 1, overflow: 'auto', scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
        {children}
      </Box>
      <Box sx={{ borderTop: '1px solid var(--tx-line)', pt: '12px' }}>{footer}</Box>
    </Box>
  );
};
